import type { JSX, ReactNode } from 'react';
import type {
  CompaniesResource,
  CompanyDetailResource,
  DealSummaryResource,
  TodoResource,
} from '@majuscule/contracts';
import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { CalendarDays } from 'lucide-react';
import { internalFetch } from '../../lib/api';
import { CRM_KEY, crmKeys, daysInStageLabel, formatNextAction } from '../../lib/crm';
import { Badge } from '@/components/ui/badge';
import { Callout } from '@/components/ui/alert';
import { cn } from '@/lib/utils';

// --- Queries ------------------------------------------------------------------

export function useCompanies(enabled = true): UseQueryResult<CompaniesResource> {
  return useQuery({
    queryKey: crmKeys.companies,
    queryFn: () => internalFetch<CompaniesResource>('/internal/crm/companies'),
    enabled,
  });
}

export function useCompany(companyId: string | null): UseQueryResult<CompanyDetailResource> {
  return useQuery({
    queryKey: crmKeys.company(companyId ?? ''),
    queryFn: () =>
      internalFetch<CompanyDetailResource>(`/internal/crm/companies/${companyId ?? ''}`),
    enabled: companyId !== null && companyId !== '',
  });
}

/** Same query (and cache) as the À faire page; also feeds the navigation badge. */
export function useTodo(): UseQueryResult<TodoResource> {
  return useQuery({
    queryKey: crmKeys.todo,
    queryFn: () => internalFetch<TodoResource>('/internal/crm/todo'),
    refetchInterval: 60_000,
  });
}

/** Refreshes every CRM view after a mutation (and the billed accounts when asked). */
export function useCrmRefresh(): (options?: { clients?: boolean }) => void {
  const queryClient = useQueryClient();
  return (options) => {
    void queryClient.invalidateQueries({ queryKey: CRM_KEY });
    if (options?.clients === true) {
      void queryClient.invalidateQueries({ queryKey: ['clients'] });
    }
  };
}

// --- Small pieces -------------------------------------------------------------

/** Error of a mutation, inside a dialog body. */
export function MutationError({ message }: { message: string | null }): JSX.Element | null {
  return message === null ? null : <Callout variant="destructive">{message}</Callout>;
}

/** `next-line` : calendar icon, bold due moment, subject. */
export function NextLine({ when, label }: { when: ReactNode; label: ReactNode }): JSX.Element {
  return (
    <span className="flex items-start gap-1.5 text-xs leading-4 text-muted-foreground">
      <CalendarDays className="mt-px size-3.5 shrink-0" aria-hidden="true" />
      <span>
        <b className="font-medium text-foreground">{when}</b> {label}
      </span>
    </span>
  );
}

/** Follow-up of an open deal: warning badges and next action. */
export function DealFollowUp({
  deal,
  compact = false,
}: {
  deal: DealSummaryResource;
  compact?: boolean;
}): JSX.Element {
  return (
    <span className="grid justify-items-start gap-1">
      {deal.flag === 'no_next_action' ? (
        <>
          <Badge variant="warning">Sans prochaine action</Badge>
          {compact ? null : (
            <span className="text-xs leading-4 text-muted-foreground">
              {daysInStageLabel(deal.daysInStage)}
            </span>
          )}
        </>
      ) : null}
      {deal.flag === 'late' ? <Badge variant="warning">Rappel en retard</Badge> : null}
      {deal.nextAction !== null ? (
        <NextLine when={formatNextAction(deal.nextAction)} label={deal.nextAction.subject} />
      ) : null}
    </span>
  );
}

/** `mini` list of side cards. */
export function MiniList({ children }: { children: ReactNode }): JSX.Element {
  return <div className="grid">{children}</div>;
}

export function MiniRow({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <div
      className={cn(
        'flex items-center justify-between gap-3 border-b border-border py-2 text-[13px] leading-[18px] last:border-b-0',
        className,
      )}
    >
      {children}
    </div>
  );
}

/** Two-line cell of a mini row. */
export function MiniCell({
  primary,
  secondary,
}: {
  primary: ReactNode;
  secondary?: ReactNode;
}): JSX.Element {
  return (
    <span className="grid min-w-0 leading-[18px]">
      <span className="min-w-0">{primary}</span>
      {secondary !== undefined && secondary !== null && secondary !== '' ? (
        <small className="text-xs leading-4 text-muted-foreground">{secondary}</small>
      ) : null}
    </span>
  );
}

/** `choices` : two pressed-state cards (deal model). */
export function Choices<T extends string>({
  value,
  onChange,
  items,
  labelledBy,
}: {
  value: T;
  onChange: (value: T) => void;
  items: readonly { value: T; label: string; hint: string }[];
  labelledBy: string;
}): JSX.Element {
  return (
    <div role="group" aria-labelledby={labelledBy} className="grid grid-cols-2 gap-2">
      {items.map((item) => (
        <button
          key={item.value}
          type="button"
          aria-pressed={item.value === value}
          onClick={() => {
            onChange(item.value);
          }}
          className={cn(
            'grid gap-0.5 rounded-md border border-border bg-card p-3 text-left text-foreground',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            'aria-pressed:border-foreground aria-pressed:ring-1 aria-pressed:ring-inset aria-pressed:ring-foreground',
          )}
        >
          <b className="text-[13px] font-medium leading-[18px]">{item.label}</b>
          <span className="text-xs leading-4 text-muted-foreground">{item.hint}</span>
        </button>
      ))}
    </div>
  );
}

/** Head of a detail page: back link, title, badges line. */
export function DetailHead({
  back,
  title,
  heading,
  badges,
}: {
  back: ReactNode;
  title: ReactNode;
  /** Replaces the plain `<h1>` (an editable title brings its own, see `DealTitle`). */
  heading?: ReactNode;
  badges?: ReactNode;
}): JSX.Element {
  return (
    <div className="min-w-0">
      <div className="mb-1 text-[13px] leading-[18px]">{back}</div>
      {heading ?? <h1 className="m-0 text-title">{title}</h1>}
      {badges !== undefined ? (
        <div className="mt-2 flex flex-wrap items-center gap-2">{badges}</div>
      ) : null}
    </div>
  );
}

export const backLinkClass =
  'inline-flex items-center gap-1 text-muted-foreground no-underline hover:text-foreground [&_svg]:size-3.5';
